import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useProfile as useSharedProfile } from "../shared/hooks/useProfile";
import { invalidateCache } from "../utils/apiCache";

// ---------------------------------------------------------------------------
// Read
// ---------------------------------------------------------------------------

/** Shared cached profile query. staleTime: Infinity — only refreshed on
 *  explicit cache patch or bust. */
export const useProfile = useSharedProfile;

// ---------------------------------------------------------------------------
// Mutations — each patches the React Query ["profile"] cache on success
// so the UI reflects changes instantly without a re-fetch.
// ---------------------------------------------------------------------------

/**
 * Save Summary & Skills via V2 endpoint.
 * Payload: { ProfessionalSummary, Technologies, Certifications, Interests, Publications }
 * Response: UpdateSummarySkillsResponseDto (camelCase fields)
 */
export const useSaveSummarySkills = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      const token = localStorage.getItem("token");
      const res = await fetch("/api/v2/profile/summary/update-skills", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.text().catch(() => res.statusText);
        throw new Error(err || "Failed to save summary & skills");
      }
      return res.json();
    },
    onSuccess: (data) => {
      // Bust in-memory apiCache so next cold-load fetches fresh data
      invalidateCache(["/api/profile/get"]);

      // Patch React Query cache with what the backend actually saved
      queryClient.setQueryData(["profile"], (old) => {
        if (!old) return old;
        return {
          ...old,
          record: {
            ...old.record,
            professional_summary:
              data.professionalSummary ?? old.record?.professional_summary,
            technologies: data.technologies ?? old.record?.technologies,
            interests: data.interests ?? old.record?.interests,
            certifications: data.certifications ?? old.record?.certifications,
            publications: data.publications ?? old.record?.publications,
          },
        };
      });
    },
  });
};

/**
 * Save Education and Projects via V2 endpoint.
 * ONLY touches education and projects — skills/certifications/interests are never sent.
 */
export const useSaveEducationProjects = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      const token = localStorage.getItem("token");
      const res = await fetch("/api/v2/profile/education/update", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.text().catch(() => res.statusText);
        throw new Error(err || "Failed to save education and projects");
      }
      return res.json();
    },
    onSuccess: (data, payload) => {
      // Patch only education and projects in the React Query cache — nothing else
      queryClient.setQueryData(["profile"], (old) => {
        if (!old) return old;
        return {
          ...old,
          record: {
            ...old.record,
            ...(payload.Education !== undefined && { education: payload.Education }),
            ...(payload.Projects !== undefined && { projects: payload.Projects }),
          },
        };
      });
    },
  });
};


/**
 * Save Highlights (education, projects, certs, techs, interests, publications)
 * via the /save-highlights endpoint.
 * The endpoint returns no meaningful body, so we patch the cache from the
 * outgoing payload directly.
 */
export const useSaveHighlights = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      const token = localStorage.getItem("token");
      const res = await fetch("/api/profile/save-highlights", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.text().catch(() => res.statusText);
        throw new Error(err || "Failed to save highlights");
      }
      return payload; // endpoint returns no body; use what we sent
    },
    onSuccess: (payload) => {
      // Do NOT invalidate apiCache here — it would trigger a refetch that
      // returns raw DB data (possibly object arrays) and wipe the skills cache.
      // The React Query patch below is sufficient for instant UI consistency.

      queryClient.setQueryData(["profile"], (old) => {
        if (!old) return old;
        return {
          ...old,
          record: {
            ...old.record,
            ...(payload.Certifications !== undefined && {
              certifications: payload.Certifications,
            }),
            ...(payload.Technologies !== undefined && {
              technologies: payload.Technologies,
            }),
            ...(payload.Interests !== undefined && {
              interests: payload.Interests,
            }),
            ...(payload.Education !== undefined && {
              education: payload.Education,
            }),
            ...(payload.Projects !== undefined && {
              projects: payload.Projects,
            }),
            ...(payload.Publications !== undefined && {
              publications: payload.Publications,
            }),
          },
        };
      });
    },
  });
};

// ---------------------------------------------------------------------------
// Utility mutations (not profile-cache-aware — simple fire-and-forget)
// ---------------------------------------------------------------------------

/** Update the onboarding landing flag. */
export const useUpdateLandingFlag = () => {
  const [isPending, setIsPending] = useState(false);

  const mutateAsync = async (landingFlag) => {
    try {
      setIsPending(true);
      const token = localStorage.getItem("token");
      const res = await fetch("/api/landing/update", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ landingFlag }),
      });
      if (!res.ok) {
        const err = await res.text();
        throw new Error(err || "Failed to update landing flag");
      }
      return res.json();
    } finally {
      setIsPending(false);
    }
  };

  return { mutateAsync, isPending };
};
